import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import useStyle from "./FileUploadRenderer.styles";
import { getFileSize } from "../../../utils/index";

interface Props extends RendererProps {
    variableName?: string;
    fileName?: string;
    fileSize?: number;
    fileType?: string;
    fileMime?: string;
    fileStatus?: string;
    fileBlob?: Blob;
    fileProgress?: number;
    isValid?: boolean;
    onFileOpen?: React.MouseEventHandler;
    onClearFile?: Function;
    onFileSelection?: (file: File) => void;
    browseFilesLabel?: string;
    dragFilesLabel?: string;
    uploadErrorMessage?: string;
}

export default function FileUploadRenderer(props: Props) {
    const {
        fileName,
        fileSize,
        fileMime,
        fileBlob,
        fileProgress,
        isValid,
        onFileOpen,
        onFileSelection,
        variableName,
        browseFilesLabel,
        dragFilesLabel: dragFileLabel,
        uploadErrorMessage,
    } = props;
    const theme = useTheme();
    const classes = useStyle({ theme });
    const [isDragOver, setIsDragOver] = React.useState(false);
    const [dragCounter, setDragCounter] = React.useState(0);

    const isFileSelected = fileName !== undefined && fileName !== "";
    let browseBtn;

    function onFileSelected(file: File) {
        if (file) onFileSelection(file);
    }

    function onChange(event: React.ChangeEvent<HTMLInputElement>) {
        onFileSelected(event.target.files[0]);
    }

    function onDrop(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            onFileSelected(event.dataTransfer.files[0]);
        }

        setIsDragOver(false);
        setDragCounter(0);
    }

    function onDragOver(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    function onDragEnter(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        setDragCounter(dragCounter + 1);
        setIsDragOver(true);
    }

    function onDragExit(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        const counterValue = dragCounter - 1;
        setDragCounter(counterValue);
        if (counterValue > 0) return;
        setIsDragOver(false);
    }

    function onClearFile() {
        props.onClearFile();
    }

    function onKeyPress(event, func: Function) {
        if (event.key === " " || event.key === "Enter") {
            if (func) {
                func();
            }
            event.preventDefault();
        }
    }

    function getFilePreview() {
        switch (fileMime.split("/").shift()) {
            case "image":
                if (!fileBlob) return <FontAwesomeIcon icon={["far", "file-image"]} />;
                return fileBlob && <img alt="File preview" src={URL.createObjectURL(fileBlob)} />;
            case "video":
                return <FontAwesomeIcon icon={["far", "file-video"]} />;
            case "audio":
                return <FontAwesomeIcon icon={["far", "file-audio"]} />;
            case "text":
                return <FontAwesomeIcon icon={["far", "file-alt"]} />;
            case "application":
                switch (fileMime.split("/").pop()) {
                    case "pdf":
                        return <FontAwesomeIcon icon={["far", "file-pdf"]} />;
                    case "x-zipped-compressed":
                        return <FontAwesomeIcon icon={["far", "file-archive"]} />;
                    case "vnd.ms-excel":
                    case "vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                        return <FontAwesomeIcon icon={["far", "file-excel"]} />;
                    case "msword":
                    case "vnd.openxmlformats-officedocument.wordprocessingml.document":
                        return <FontAwesomeIcon icon={["far", "file-word"]} />;
                    case "vnd.ms-powerpoint":
                    case "vnd.openxmlformats-officedocument.presentationml.presentation":
                        return <FontAwesomeIcon icon={["far", "file-powerpoint"]} />;
                    default:
                        return <FontAwesomeIcon icon={["far", "file"]} />;
                }
            default:
                return <FontAwesomeIcon icon={["far", "file"]} />;
        }
    }

    return (
        <div className="file-upload__question">
            <div
                className={classnames(
                    classes.fileUploadContainer,
                    isDragOver && classes.fileUploadContainerDrag,
                )}
                onDrop={onDrop}
                onDragEnter={onDragEnter}
                onDragExit={onDragExit}
                onDragLeave={onDragExit}
                onDragOver={onDragOver}
            >
                <div className={classes.fileUploadIcon}>
                    <FontAwesomeIcon icon={["far", "cloud-upload"]} size="2x" />
                </div>

                <div className={classes.fileUploadOptionText}>{dragFileLabel}</div>
                <div
                    className={classes.fileUploadButton}
                    tabIndex={0}
                    onKeyPress={(event) => {
                        onKeyPress(event, () => browseBtn.click());
                    }}
                >
                    <label htmlFor={`${variableName}_INPUT`}>
                        <input
                            id={`${variableName}_INPUT`}
                            type="file"
                            className="form-control"
                            hidden
                            onChange={onChange}
                            ref={(el) => (browseBtn = el)}
                        />
                        {browseFilesLabel}
                    </label>
                </div>
            </div>
            {isFileSelected && (
                <div className={classes.fileUploadSelectedFile}>
                    {isValid && fileProgress < 1 && (
                        <div
                            className={classes.fileUploadProgress}
                            style={{
                                width: `${fileProgress * 100}%`,
                            }}
                        />
                    )}
                    <div className={classes.fileUploadFileInfo}>
                        <div className={classes.fileUploadFileContainer}>
                            <div
                                className={classes.fileUploadFileContainerInner}
                                onClick={onFileOpen}
                                role="button"
                                tabIndex={-1}
                            >
                                <div className={classes.fileUploadFileIcon}>{getFilePreview()}</div>
                                <div className={classes.fileUploadFileDetails}>
                                    <div className={classes.fileUploadFileName}>{fileName}</div>
                                    <div className={classes.fileUploadFileSize}>
                                        {getFileSize(fileSize)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {isValid ? (
                            <div className={classes.fileUploadProgressText}>
                                {(fileProgress * 100).toFixed(0)}%
                            </div>
                        ) : (
                            <div className={classes.fileUploadValidationError}>
                                <FontAwesomeIcon
                                    icon={["far", "exclamation-triangle"]}
                                    className={classes.fileUploadValidationErrorIcon}
                                />
                                {uploadErrorMessage}
                            </div>
                        )}
                        <div
                            className={classes.fileUploadDeleteButton}
                            onClick={onClearFile}
                            onKeyPress={(event) => {
                                onKeyPress(event, onClearFile);
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <FontAwesomeIcon icon="times-circle" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
